import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { EntityRefLink } from '@backstage/plugin-catalog-react';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';

import {
  getSurfaceBorder,
  getSurfaceColor,
  getThemeTokens,
} from '../../../theme/tokens';
import { renderLifecycleBadge } from '../../entity-explorer/shared';
import { EntityPanelFrame } from './EntityPanelFrame';
import { useEntityPageModel } from './useEntityPageModel';

const EntityAboutField = ({
  children,
  label,
}: {
  children: React.ReactNode;
  label: string;
}) => {
  const theme = useTheme();
  const tokens = getThemeTokens(theme);

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 0.625,
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          color: tokens.textMuted,
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: '0.06em',
          lineHeight: 1.2,
          textTransform: 'uppercase',
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          alignItems: 'center',
          color: tokens.textPrimary,
          display: 'flex',
          minHeight: 24,
          minWidth: 0,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

const TagList = ({ tags }: { tags: string[] }) => {
  const theme = useTheme();
  const tokens = getThemeTokens(theme);

  if (!tags.length) {
    return (
      <Typography sx={{ color: tokens.textMuted, fontSize: 14 }}>—</Typography>
    );
  }

  return (
    <Stack direction="row" flexWrap="wrap" spacing={0.875} useFlexGap>
      {tags.map(tag => (
        <Box
          key={tag}
          component="span"
          sx={{
            alignItems: 'center',
            backgroundColor: getSurfaceColor(theme, 'subtle'),
            border: `1px solid ${getSurfaceBorder(theme)}`,
            borderRadius: 999,
            color: tokens.textSecondary,
            display: 'inline-flex',
            fontSize: 12,
            fontWeight: 600,
            lineHeight: 1,
            px: 1.2,
            py: 0.65,
          }}
        >
          {tag}
        </Box>
      ))}
    </Stack>
  );
};

export const EntityAboutPanel = () => {
  const theme = useTheme();
  const tokens = getThemeTokens(theme);
  const model = useEntityPageModel();

  return (
    <EntityPanelFrame title="About">
      <Stack spacing={2.5}>
        {model.hero.description ? (
          <Typography
            sx={{
              color: tokens.textSecondary,
              fontSize: 16,
              lineHeight: 1.75,
              maxWidth: 760,
            }}
          >
            {model.hero.description}
          </Typography>
        ) : null}

        <Box
          sx={{
            display: 'grid',
            gap: 2.5,
            gridTemplateColumns: {
              lg: 'repeat(2, minmax(0, 1fr))',
              xs: 'minmax(0, 1fr)',
            },
          }}
        >
          <EntityAboutField label="Owner">
            {model.about.owner.reference ? (
              <EntityRefLink
                entityRef={model.about.owner.reference.entityRef}
                title={model.about.owner.reference.title}
              />
            ) : (
              <Typography sx={{ color: tokens.textMuted, fontSize: 14 }}>
                —
              </Typography>
            )}
          </EntityAboutField>

          <EntityAboutField label="Lifecycle">
            {model.about.lifecycle.value ? (
              renderLifecycleBadge(model.about.lifecycle.value)
            ) : (
              <Typography sx={{ color: tokens.textMuted, fontSize: 14 }}>
                —
              </Typography>
            )}
          </EntityAboutField>

          <EntityAboutField label="System">
            {model.about.system.reference ? (
              <EntityRefLink
                entityRef={model.about.system.reference.entityRef}
                title={model.about.system.reference.title}
              />
            ) : (
              <Typography sx={{ color: tokens.textMuted, fontSize: 14 }}>
                —
              </Typography>
            )}
          </EntityAboutField>

          <EntityAboutField label="Repository">
            {model.about.repository.value ? (
              <Link
                href={model.about.repository.value}
                rel="noreferrer"
                sx={{
                  alignItems: 'center',
                  color: theme.palette.primary.main,
                  display: 'inline-flex',
                  fontSize: 14,
                  fontWeight: 700,
                  gap: 0.75,
                  textDecoration: 'none',
                }}
                target="_blank"
                underline="none"
              >
                {model.about.repository.value
                  .replace(/^https?:\/\//, '')
                  .replace(/^github\.com\//, '')}
                <OpenInNewOutlinedIcon fontSize="inherit" />
              </Link>
            ) : (
              <Typography sx={{ color: tokens.textMuted, fontSize: 14 }}>
                —
              </Typography>
            )}
          </EntityAboutField>

          <EntityAboutField label="Type">
            <Typography sx={{ color: tokens.textPrimary, fontSize: 15 }}>
              {model.about.type.value ?? '—'}
            </Typography>
          </EntityAboutField>

          <EntityAboutField label="Language">
            <Typography sx={{ color: tokens.textPrimary, fontSize: 15 }}>
              {model.about.language.value ?? '—'}
            </Typography>
          </EntityAboutField>
        </Box>

        <EntityAboutField label="Tags">
          <TagList tags={model.about.tags} />
        </EntityAboutField>
      </Stack>
    </EntityPanelFrame>
  );
};
